import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { addComponentState, removeComponentState, useScreen } from "../../core/Global";

type BottomSheetProps = {
    id: string
    open: boolean
    onClose?: () => void
    children: React.ReactNode
    minHeight?: number
    maxHeight?: number
};

export const BottomSheet: React.FC<BottomSheetProps> = ({
    id,
    open,
    onClose,
    children,
    minHeight,
    maxHeight
}) => {
    const [mounted, setMounted] = useState(false);
    const [animOpen, setAnimOpen] = useState(false);
    const sheetRef = useRef<HTMLDivElement>(null);
    const startY = useRef<number | null>(null);
    const currentY = useRef<number>(0);
    const dragging = useRef(false);
    const screen = useScreen()

    // mount/unmount + body scroll lock
    useEffect(() => {
        if (open) {
            setMounted(true);
            requestAnimationFrame(() => setAnimOpen(true));
            const prev = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            return () => { document.body.style.overflow = prev; };
        } else if (mounted) {
            // play close anim then unmount
            setAnimOpen(false);
            const t = setTimeout(() => setMounted(false), 220);
            return () => clearTimeout(t);
        }
    }, [open, mounted]);

    useEffect(() => {

        addComponentState({
            id,
            close: () => onClose?.()
        })

        return () => {
            removeComponentState(id);
        }

    }, [])

    if (!mounted) return null;

    const handleBackdrop = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget && onClose) onClose();
    };

    // touch-drag to close
    const onTouchStart = (e: React.TouchEvent) => {
        dragging.current = true;
        startY.current = e.touches[0].clientY;
        currentY.current = 0;
    };
    const onTouchMove = (e: React.TouchEvent) => {
        if (!dragging.current || startY.current == null) return;
        const dy = e.touches[0].clientY - startY.current;
        if (dy > 0) {
            currentY.current = dy;
            if (sheetRef.current) {
                sheetRef.current.style.transform = `translateY(${dy}px)`;
            }
        }
    };
    const onTouchEnd = () => {
        dragging.current = false;
        const dy = currentY.current;
        if (sheetRef.current) {
            sheetRef.current.style.transform = "";
        }
        // اگر به اندازه کافی کشیده شد، ببند
        if (dy > 80 && onClose) onClose();
    };

    if (!screen.isMobile)
        return (<></>)

    return createPortal(
        <div id={id}
            role="dialog"
            aria-modal="true"
            onClick={handleBackdrop}
            style={backdrop(animOpen)}
        >
            <div
                ref={sheetRef}
                style={sheet(maxHeight, minHeight, animOpen)}

            >
                <div style={grabberWrap}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}>
                    <div style={grabber} />
                </div>
                <div style={content}>
                    {children}
                </div>
            </div>
        </div>,
        document.body
    );
};

// styles
const backdrop = (open: boolean): React.CSSProperties => ({
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.35)",
    backdropFilter: "saturate(120%) blur(2px)",
    opacity: open ? 1 : 0,
    transition: "opacity 200ms ease",
    zIndex: 9999,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
});

const sheet = (maxHeight?: number, minHeight?: number, open?: boolean): React.CSSProperties => ({
    width: "100%",
    maxWidth: 600,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    background: "#fff",
    boxShadow: "0 -12px 30px rgba(0,0,0,0.18)",
    transform: open ? "translateY(0)" : "translateY(24px)",
    transition: "transform 220ms ease",

    maxHeight: maxHeight ?? "90vh",
    minHeight: minHeight ?? 150,

    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
});


const grabberWrap: React.CSSProperties = {
    display: "flex",
    justifyContent: "center",
    paddingTop: 8,
    flexShrink: 0,
};
const grabber: React.CSSProperties = {
    width: 40,
    height: 5,
    borderRadius: 999,
    background: "#D1D5DB",
};
const content: React.CSSProperties = {
    padding: "12px 16px 20px",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    flex: 1,
    minHeight: 0
};
